import React from "react";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";
import {
  GetAllVendorsParams,
  IntegrationsResponse,
} from "features/integrations/integrations";
import { useNavigate } from "react-router-dom";
import { useViewAs } from "hooks/useViewAs";
import { formatFreqAndTime, formatIntegrationDateTime } from "utils/dateUtils";
import CustomScrollbars from "../CustomScrollbars";

const noWrapCellStyle = {
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const COLUMN_COUNT = 8;

function SkeletonRows() {
  return (
    <>
      {Array.from({ length: 8 }).map((_, i) => (
        <TableRow key={i}>
          {Array.from({ length: COLUMN_COUNT }).map((_, j) => (
            <TableCell key={j}>
              <Skeleton variant="text" width={j === 2 ? 80 : "80%"} />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );
}

type IntegrationsTableProps = {
  integrations: IntegrationsResponse[];
  isLoading: boolean;
  isFetching: boolean;
  error?: unknown;
  sortBy: GetAllVendorsParams["sort_by"];
  sortOrder: "asc" | "desc";
  onSortChange: (field: GetAllVendorsParams["sort_by"]) => void;
  useIntegrationTimezone?: boolean;
  pagination?: React.ReactNode;
};

export default function IntegrationsTable({
  integrations,
  isLoading,
  isFetching,
  error,
  sortBy,
  sortOrder,
  onSortChange,
  useIntegrationTimezone = true,
  pagination,
}: IntegrationsTableProps) {
  const navigate = useNavigate();
  const { href } = useViewAs();

  if (error) {
    return (
      <Typography p={2} variant="h6" align="center" color="error">
        Error loading integrations
      </Typography>
    );
  }

  const safeIntegrations = (
    Array.isArray(integrations) ? integrations : []
  ).filter(
    (item): item is IntegrationsResponse =>
      item !== null &&
      item !== undefined &&
      typeof item === "object" &&
      "client" in item &&
      "integration" in item &&
      "type" in item,
  );

  const handleRowClick = (integrationId: number) => {
    navigate(href(`/integrations/${integrationId}`));
  };

  const columns: {
    key: GetAllVendorsParams["sort_by"] | "frequency_time";
    label: string;
  }[] = [
    { key: "client", label: "Client" },
    { key: "integration", label: "Integration" },
    { key: "type", label: "Type" },
    { key: "enabled", label: "Enabled" },
    { key: "frequency_time", label: "Frequency/Time" },
    { key: "last_run", label: "Last Run" },
    { key: "next_run", label: "Next Run" },
    { key: "completed", label: "Completed" },
  ];

  return (
    <Paper
      elevation={0}
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "clip",
      }}
    >
      <CustomScrollbars
        sx={{ flex: 1, minWidth: 0, overflowX: "auto" }}
        trackOffsetTop={40}
        trackOffsetBottom={2}
      >
        <Table
          stickyHeader
          size="small"
          sx={{ minWidth: 700, borderColor: "divider" }}
        >
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell key={col.key} sx={{ fontWeight: 700 }}>
                  <TableSortLabel
                    active={
                      sortBy === col.key ||
                      (col.key === "frequency_time" && sortBy === "frequency")
                    }
                    direction={sortBy === col.key ? sortOrder : "asc"}
                    onClick={() =>
                      onSortChange(
                        col.key === "frequency_time" ? "frequency" : col.key,
                      )
                    }
                  >
                    {col.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <SkeletonRows />
            ) : safeIntegrations.length === 0 ? (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} align="center" sx={{ py: 6 }}>
                  <Typography color="text.secondary">
                    No integrations found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              safeIntegrations.map((row) => (
                <TableRow
                  key={row.id}
                  onClick={() => handleRowClick(row.id)}
                  sx={{
                    "&:last-child td, &:last-child th": {
                      borderBottom: "none",
                    },
                    opacity: isFetching ? 0.6 : 1,
                    cursor: "pointer",
                    "&:hover": {
                      bgcolor: "calibrate.primarySoft06",
                    },
                  }}
                >
                  <TableCell sx={noWrapCellStyle}>{row.client}</TableCell>
                  <TableCell sx={noWrapCellStyle}>{row.integration}</TableCell>
                  <TableCell>{row.type}</TableCell>
                  <TableCell>
                    {row.enabled === true ? (
                      <CheckIcon
                        sx={{ color: "secondary.main", fontSize: 20 }}
                      />
                    ) : (
                      <CloseIcon
                        sx={{ color: "text.disabled", fontSize: 20 }}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Box
                      display="flex"
                      flexDirection="column"
                      alignItems="flex-start"
                    >
                      {formatFreqAndTime(row).map((item, idx) => (
                        <Typography key={idx} variant="body2">
                          {item}
                        </Typography>
                      ))}
                    </Box>
                  </TableCell>
                  <TableCell sx={noWrapCellStyle}>
                    {row.last_run ? (
                      <Typography variant="body2">
                        {formatIntegrationDateTime(
                          row.last_run,
                          useIntegrationTimezone ? row.timezone : undefined,
                        )}
                      </Typography>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell sx={noWrapCellStyle}>
                    {row.next_run ? (
                      <Typography variant="body2">
                        {formatIntegrationDateTime(
                          row.next_run,
                          useIntegrationTimezone ? row.timezone : undefined,
                        )}
                      </Typography>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell>
                    {row.completed ? (
                      <CheckIcon
                        sx={{ color: "secondary.main", fontSize: 20 }}
                      />
                    ) : (
                      <CloseIcon sx={{ color: "error.main", fontSize: 20 }} />
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </CustomScrollbars>
      {pagination}
    </Paper>
  );
}
